//auth 2.0
import * as Dom from "./utils/dom";
import Caret from "./utils/caret";
import "./index.css";

/**
 * @typedef {object} ListData
 * @property {string} style - can be ordered or unordered
 * @property {Array} items - li elements
 */

/**
 * List Tool for the Editor.js 2.0
 */
class BlockList {
  /**
   * Notify core that read-only mode is supported
   *
   * @returns {boolean}
   */
  static get isReadOnlySupported() {
    return true;
  }

  /**
   * Allow to use native Enter behaviour
   *
   * @returns {boolean}
   * @public
   */
  static get enableLineBreaks() {
    return true;
  }

  /**
   * Render plugin`s main Element and fill it with saved data
   *
   * @param {object} params - tool constructor options
   * @param {ListData} params.data - previously saved data
   * @param {object} params.config - user config for Tool
   * @param {object} params.api - Editor.js API
   * @param {boolean} params.readOnly - read-only mode flag
   */
  constructor({ data, config, api, readOnly }) {
    /**
     * HTML nodes
     *
     * @private
     */
    this._elements = {
      wrapper: null,
    };

    this.config = config;

    this.api = api;
    this.readOnly = readOnly;

    this.settings = [
      {
        name: "unordered",
        title: this.api.i18n.t("Unordered"),
        icon: '<svg width="15" height="14" viewBox="0 0 15 14" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 2.5C1.55228 2.5 2 2.05228 2 1.5C2 0.947715 1.55228 0.5 1 0.5C0.447715 0.5 0 0.947715 0 1.5C0 2.05228 0.447715 2.5 1 2.5ZM4.5 1C4.22386 1 4 1.22386 4 1.5C4 1.77614 4.22386 2 4.5 2H14.5C14.7761 2 15 1.77614 15 1.5C15 1.22386 14.7761 1 14.5 1H4.5ZM4.5 12C4.22386 12 4 12.2239 4 12.5C4 12.7761 4.22386 13 4.5 13H14.5C14.7761 13 15 12.7761 15 12.5C15 12.2239 14.7761 12 14.5 12H4.5ZM4.5 6.5C4.22386 6.5 4 6.72386 4 7C4 7.27614 4.22386 7.5 4.5 7.5H14.5C14.7761 7.5 15 7.27614 15 7C15 6.72386 14.7761 6.5 14.5 6.5H4.5ZM1 8C1.55228 8 2 7.55228 2 7C2 6.44772 1.55228 6 1 6C0.447715 6 0 6.44772 0 7C0 7.55228 0.447715 8 1 8ZM2 12.5C2 13.0523 1.55228 13.5 1 13.5C0.447715 13.5 0 13.0523 0 12.5C0 11.9477 0.447715 11.5 1 11.5C1.55228 11.5 2 11.9477 2 12.5Z" fill="currentColor" /></svg>',
        default: true,
      },
      {
        name: "ordered",
        title: this.api.i18n.t("Ordered"),
        icon: '<svg width="17" height="13" viewBox="0 0 17 13" xmlns="http://www.w3.org/2000/svg"><path d="M5.819 4.607h9.362a1.069 1.069 0 0 1 0 2.138H5.82a1.069 1.069 0 1 1 0-2.138zm0-4.607h9.362a1.069 1.069 0 0 1 0 2.138H5.82a1.069 1.069 0 1 1 0-2.138zm0 9.357h9.362a1.069 1.069 0 0 1 0 2.138H5.82a1.069 1.069 0 0 1 0-2.137zM1.468 4.155V1.33c-.554.404-.926.606-1.118.606a.338.338 0 0 1-.244-.104A.327.327 0 0 1 0 1.59c0-.107.035-.184.105-.234.07-.05.192-.114.369-.192.264-.118.475-.243.633-.373.158-.13.298-.276.42-.438a3.94 3.94 0 0 1 .238-.298C1.802.019 1.872 0 1.975 0c.115 0 .208.042.277.127.07.085.105.202.105.351v3.556c0 .416-.15.624-.448.624a.421.421 0 0 1-.32-.127c-.08-.085-.121-.21-.121-.376zm-.283 6.664h1.572c.156 0 .275.03.358.091a.294.294 0 0 1 .123.25.323.323 0 0 1-.098.238c-.065.065-.164.097-.296.097H.629a.494.494 0 0 1-.353-.119.372.372 0 0 1-.126-.28c0-.068.027-.16.081-.273a.977.977 0 0 1 .178-.268c.267-.264.507-.49.722-.678.215-.188.368-.312.46-.371.165-.11.302-.222.412-.334.109-.112.192-.226.25-.344a.786.786 0 0 0 .085-.345.6.6 0 0 0-.341-.553.75.75 0 0 0-.345-.08c-.263 0-.47.11-.62.329-.02.029-.054.107-.101.235a.966.966 0 0 1-.16.295c-.059.069-.145.103-.26.103a.348.348 0 0 1-.25-.094.34.34 0 0 1-.099-.258c0-.132.031-.27.093-.413.063-.143.155-.273.279-.39.123-.116.28-.21.47-.282.189-.072.411-.107.666-.107.307 0 .569.045.786.137a1.182 1.182 0 0 1 .618.623 1.18 1.18 0 0 1-.096 1.083 2.03 2.03 0 0 1-.378.457c-.128.11-.344.282-.646.517-.302.235-.509.417-.621.547a1.637 1.637 0 0 0-.148.187z" fill="currentColor" /></svg>',
        default: false,
      },
    ];

    /**
     * Tool's data
     *
     * @type {ListData}
     */
    this._data = {
      style: this.settings.find((tune) => tune.default === true).name,
      items: [],
      indentLevel: data.indentLevel || 1, // 1 ~ 8
    };

    this.MAX_INDENT_LEVEL = 8;
    this.INDENT_PADDING = 30;

    this.data = data;
  }

  onKeyDown(event) {
    const [ENTER, BACKSPACE, TAB] = [13, 8, 9]; // key codes
    switch (event.keyCode) {
      case ENTER:
        this.addNewBlockList(event);
        // this.getOutofList(event);
        break;
      case BACKSPACE:
        this.backspace(event);
        break;
      case TAB:
        if (event.shiftKey) {
          this.shiftTab(event);
        } else {
          this.addTab(event);
        }
        break;
      default:
        return;
    }
  }

  /**
   * Returns list tag with items
   *
   * @returns {Element}
   * @public
   */
  render() {
    this._elements.wrapper = this.makeMainTag(this._data.style);

    // fill with data
    if (this._data.items.length) {
      this._data.items.forEach((item) => {
        this._elements.wrapper.appendChild(
          this._make("li", this.CSS.item, {
            innerHTML: item,
          })
        );
      });

      this._elements.wrapper.style.marginLeft = `${this.INDENT_PADDING * this._data.indentLevel}px`;
    } else {
      this._elements.wrapper.appendChild(this._make("li", this.CSS.item));
    }

    if (!this.readOnly) {
      // detect keydown on the last item to escape List
      this.onKeyDown = this.onKeyDown.bind(this);
      this._elements.wrapper.addEventListener("keydown", this.onKeyDown, false);
    }

    return this._elements.wrapper;
  }

  /**
   * @returns {ListData}
   * @public
   */
  save() {
    return this.data;
  }

  moved(event) {
    if (!document.getElementById("editor-container")) return;
    const currentScrollTop = document.getElementById("editor-container").scrollTop;
    const changedHeight = this.api.blocks.getBlockByIndex(this.api.blocks.getCurrentBlockIndex()).holder.offsetHeight;
    if (event.detail.fromIndex < event.detail.toIndex) {
      document.getElementById("editor-container").scrollTop = currentScrollTop + changedHeight;
    } else {
      document.getElementById("editor-container").scrollTop = currentScrollTop - changedHeight;
    }
  }

  /**
   * Sanitizer rules
   *
   * @returns {object}
   */
  static get sanitize() {
    return {
      style: {},
      items: {
        br: true,
      },
    };
  }

  /**
   * Settings
   *
   * @public
   * @returns {Element}
   */
  // renderSettings() {
  //   const wrapper = this._make('div', [ this.CSS.settingsWrapper ], {});

  //   this.settings.forEach((item) => {
  //     const itemEl = this._make('div', this.CSS.settingsButton, {
  //       innerHTML: item.icon,
  //     });

  //     itemEl.addEventListener('click', () => {
  //       this.toggleTune(item.name);

  //       // clear other buttons
  //       const buttons = itemEl.parentNode.querySelectorAll('.' + this.CSS.settingsButton);

  //       Array.from(buttons).forEach((button) =>
  //         button.classList.remove(this.CSS.settingsButtonActive)
  //       );

  //       // mark active
  //       itemEl.classList.toggle(this.CSS.settingsButtonActive);
  //     });

  //     this.api.tooltip.onHover(itemEl, item.title, {
  //       placement: 'top',
  //       hidingDelay: 500,
  //     });

  //     if (this._data.style === item.name) {
  //       itemEl.classList.add(this.CSS.settingsButtonActive);
  //     }

  //     wrapper.appendChild(itemEl);
  //   });

  //   return wrapper;
  // }

  /**
   * Creates main <ul> or <ol> tag depended on style
   *
   * @param {string} style - 'ordered' or 'unordered'
   * @returns {HTMLOListElement|HTMLUListElement}
   */
  makeMainTag(style) {
    const styleClass = style === "ordered" ? this.CSS.wrapperOrdered : this.CSS.wrapperUnordered;
    const tag = style === "ordered" ? "ol" : "ul";

    return this._make(tag, [this.CSS.baseBlock, this.CSS.wrapper, styleClass], {
      contentEditable: !this.readOnly,
    });
  }

  /**
   * Toggles List style
   *
   * @param {string} style - 'ordered'|'unordered'
   */
  toggleTune(style) {
    const newTag = this.makeMainTag(style);

    while (this._elements.wrapper.hasChildNodes()) {
      newTag.appendChild(this._elements.wrapper.firstChild);
    }

    this._elements.wrapper.replaceWith(newTag);
    this._elements.wrapper = newTag;
    this._data.style = style;
  }

  /**
   * Styles
   *
   * @private
   */
  get CSS() {
    return {
      baseBlock: this.api.styles.block,
      wrapper: "cdx-block-list",
      wrapperOrdered: "cdx-block-list--ordered",
      wrapperUnordered: "cdx-block-list--unordered",
      item: "cdx-block-list__item",
      settingsWrapper: "cdx-block-list-settings",
      settingsButton: this.api.styles.settingsButton,
      settingsButtonActive: this.api.styles.settingsButtonActive,
    };
  }

  /**
   * List data setter
   *
   * @param {ListData} listData
   */
  set data(listData) {
    if (!listData) {
      listData = {};
    }

    this._data.style = listData.style || this.settings.find((tune) => tune.default === true).name;
    this._data.items = listData.items || [];

    const oldView = this._elements.wrapper;

    if (oldView) {
      oldView.parentNode.replaceChild(this.render(), oldView);
    }
  }

  /**
   * Return List data
   *
   * @returns {ListData}
   */
  get data() {
    this._data.items = [];

    const items = this._elements.wrapper.querySelectorAll(`.${this.CSS.item}`);

    for (let i = 0; i < items.length; i++) {
      const value = items[i].innerHTML.replace("<br>", " ").trim();

      if (value) {
        this._data.items.push(items[i].innerHTML);
      }
    }

    return this._data;
  }

  /**
   * Helper for making Elements with attributes
   *
   * @param  {string} tagName           - new Element tag name
   * @param  {Array|string} classNames  - list or name of CSS classname(s)
   * @param  {object} attributes        - any attributes
   * @returns {Element}
   */
  _make(tagName, classNames = null, attributes = {}) {
    const el = document.createElement(tagName);
    el.spellcheck = false;

    if (Array.isArray(classNames)) {
      el.classList.add(...classNames);
    } else if (classNames) {
      el.classList.add(classNames);
    }

    for (const attrName in attributes) {
      el[attrName] = attributes[attrName];
    }

    return el;
  }

  /**
   * Returns current List item by the caret position
   *
   * @returns {Element}
   */
  get currentItem() {
    let currentNode = window.getSelection().anchorNode;

    if (currentNode.nodeType !== Node.ELEMENT_NODE) {
      currentNode = currentNode.parentNode;
    }

    return currentNode.closest(`.${this.CSS.item}`);
  }

  /**
   * Get out from List Tool
   * by Enter on the empty last item
   *
   * @param {KeyboardEvent} event
   */
  getOutofList(event) {
    const items = this._elements.wrapper.querySelectorAll("." + this.CSS.item);

    /**
     * Save the last one.
     */
    if (items.length < 2) {
      return;
    }

    const lastItem = items[items.length - 1];
    const currentItem = this.currentItem;

    /** Prevent Default li generation if item is empty */
    if (currentItem === lastItem && !lastItem.textContent.trim().length) {
      /** Insert New Block and set caret */
      currentItem.parentElement.removeChild(currentItem);
      this.api.blocks.insert();
      this.api.caret.setToBlock(this.api.blocks.getCurrentBlockIndex());
      event.preventDefault();
      event.stopPropagation();
    }
  }

  addNewBlockList(event) {
    /**
     * Prevent editor.js behaviour
     */
    event.stopPropagation();

    /**
     * Prevent browser behaviour
     */
    event.preventDefault();

    const value = this._elements.wrapper.innerText;

    /** Prevent Default li generation if item is empty */
    if (value === "") {
      /** Insert New Block and set caret */
      this.api.blocks.delete();
      this.api.blocks.insert();
      this.api.caret.setToBlock(this.api.blocks.getCurrentBlockIndex());
    } else {
      /**
       * On other Enters, get content from caret till the end of the block
       * And move it to the new item
       */
      const endingFragment = Caret.extractFragmentFromCaretPositionTillTheEnd();
      const endingText = Dom.fragmentToString(endingFragment);

      /** Insert New list and set caret */
      const blockIndex = this.api.blocks.getCurrentBlockIndex();
      this.api.blocks.insert(
        "blockList",
        {
          style: this._data.style,
          items: [endingText],
          indentLevel: this._data.indentLevel,
        },
        undefined,
        blockIndex + 1,
        true
      );
      this.api.caret.setToBlock(blockIndex + 1);
    }
  }

  /**
   * Handle backspace
   *
   * @param {KeyboardEvent} event
   */
  backspace(event) {
    const items = this._elements.wrapper.querySelectorAll("." + this.CSS.item),
      firstItem = items[0];
    if (!firstItem) {
      return;
    }
    /**
     * Save the last one.
     */
    // if (items.length < 2 && !firstItem.innerHTML.replace("<br>", " ").trim()) {
    //   event.preventDefault();
    // }
    // event.preventDefault();

    const value = this._elements.wrapper.innerText;

    /** When list is empty, convert to empty paragraph block */
    if (value === "") {
      event.stopPropagation();
      this.api.blocks.delete(this.api.blocks.getCurrentBlockIndex());
      this.api.blocks.insert();
      this.api.caret.setToBlock(this.api.blocks.getCurrentBlockIndex());
    }
  }

  /**
   * Add indentation to current item
   *
   * @param {KeyboardEvent} event - keydown
   */
  addTab(event) {
    /**
     * Prevent editor.js behaviour
     */
    event.stopPropagation();

    /**
     * Prevent browser tab behaviour
     */
    event.preventDefault();

    this._data.indentLevel = this._data.indentLevel < this.MAX_INDENT_LEVEL ? this._data.indentLevel + 1 : this.MAX_INDENT_LEVEL;

    this._elements.wrapper.style.marginLeft = `${this.INDENT_PADDING * this._data.indentLevel}px`;
  }

  shiftTab(event) {
    /**
     * Prevent editor.js behaviour
     */
    event.stopPropagation();

    /**
     * Prevent browser tab behaviour
     */
    event.preventDefault();

    this._data.indentLevel = this._data.indentLevel > 1 ? this._data.indentLevel - 1 : 1;

    this._elements.wrapper.style.marginLeft = `${this.INDENT_PADDING * this._data.indentLevel}px`;
  }

  /**
   * Select LI content by CMD+A
   *
   * @param {KeyboardEvent} event
   */
  selectItem(event) {
    event.preventDefault();

    const selection = window.getSelection(),
      currentNode = selection.anchorNode.parentNode,
      currentItem = currentNode.closest("." + this.CSS.item),
      range = new Range();

    range.selectNodeContents(currentItem);

    selection.removeAllRanges();
    selection.addRange(range);
  }
}

export default BlockList;
